import React, { useCallback, useEffect, useMemo, useState } from 'react'

import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { OpenInNew } from '@material-ui/icons'

import { DrawerBodyWrapper } from '@base'
import { strings } from '@constants'
import { displayAmount } from '@utils'
import { isSalesAdmin } from '@utils/adminFilter'
import { getSupplierQuoteSummary } from '@data'
import { showLoader } from '@data/state/action'
import { toggleFileViewer } from '@data/state/action/root'
import AddressCard from '@baseV2/composite/addressCard'

import { LinkText, DetailCard, DetailItem, DetailLabel, DetailValue, DetailTable, AddressSection } from '../styles'
import ReasonCard from '../ListApprovalMetrics/ReasonCard'

const formatDate = (value) => (value ? moment(value).format('DD MMM YYYY') : '--')

// Opened from the Supplier PO approval card's "PO amount" field.
const SupplierPoDetails = ({ row, reason, enquiryId, supplierQuoteId }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	// sales admins must not see which supplier the PO belongs to
	const hideSupplierName = isSalesAdmin(user?.role, false)

	const referenceId = enquiryId || row?.reference_id
	const quoteId = supplierQuoteId || reason?.supplierQuoteId

	const [quoteSummary, setQuoteSummary] = useState(null)

	const loadQuoteSummary = useCallback(() => {
		if (!referenceId || !quoteId) return
		dispatch(showLoader(true))
		getSupplierQuoteSummary(referenceId, quoteId)
			.then((response) => setQuoteSummary(response ?? null))
			.catch(() => setQuoteSummary(null))
			.finally(() => dispatch(showLoader(false)))
	}, [dispatch, referenceId, quoteId])

	useEffect(() => {
		loadQuoteSummary()
	}, [loadQuoteSummary])

	const handleViewPo = useCallback(
		(fileUrl) => {
			if (!fileUrl) return
			dispatch(toggleFileViewer(true, { files: [fileUrl] }))
		},
		[dispatch]
	)

	const details = useMemo(() => {
		if (!quoteSummary) return []
		return [
			{ label: strings('supplier_quote_id'), value: quoteSummary.supplier_quote_id || '--' },
			// supplier identity is not disclosed to sales admins
			...(hideSupplierName ? [] : [{ label: strings('supplier_name'), value: quoteSummary.supplier_name || '--' }]),
			{ label: strings('billing_address'), value: quoteSummary.supplier_billing_address || '--' },
			{ label: strings('freight'), value: displayAmount(quoteSummary.freight || 0) },
			{ label: strings('logistics_handler'), value: quoteSummary.logistic_handler || '--' },
			{ label: strings('delivery_start_date'), value: formatDate(quoteSummary.delivery_starting_date) },
			{ label: strings('expected_delivery_date'), value: formatDate(quoteSummary.expected_delivery_date) },
			{ label: strings('transit_time'), value: `${quoteSummary.transit_time || 0} ${strings('days')}` },
			{
				label: strings('buyer_purchase_order'),
				value: quoteSummary.buyer_purchase_order_link ? (
					<LinkText as='span' onClick={() => handleViewPo(quoteSummary.buyer_purchase_order_link)}>
						{strings('view_document')}
						<OpenInNew />
					</LinkText>
				) : (
					'--'
				),
			},
		]
	}, [quoteSummary, hideSupplierName, handleViewPo])

	const columns = useMemo(
		() => [
			{
				label: strings('product'),
				key: (item) => item?.product_name || '--',
				sortable: false,
			},
			{
				label: strings('grade'),
				key: (item) => item?.grade || '--',
				sortable: false,
			},
			{
				label: strings('price'),
				key: (item) => displayAmount(item?.price || 0),
				sortable: false,
			},
			{
				label: strings('quantity'),
				key: (item) => item?.quantity ?? '--',
				sortable: false,
			},
			{
				label: strings('amount'),
				key: (item) => displayAmount(item?.amount || 0),
				sortable: false,
			},
		],
		[]
	)

	return (
		<DrawerBodyWrapper>
			{/* same header the messages drawer uses */}
			<ReasonCard row={row} reason={reason} hideActions statusTopRight />
			{!!details.length && (
				<DetailCard hoverable={false}>
					{details.map((detail) => (
						<DetailItem key={detail.label} $fullRow={detail.fullRow}>
							<DetailLabel>{detail.label}</DetailLabel>
							<DetailValue>{detail.value}</DetailValue>
						</DetailItem>
					))}
					{/* payment terms is long free text — always given a row of its own */}
					<DetailItem $fullRow>
						<DetailLabel>{strings('supplier_payment_term')}</DetailLabel>
						<DetailValue>{quoteSummary?.supplier_payment_term || '--'}</DetailValue>
					</DetailItem>
				</DetailCard>
			)}
			<DetailTable columns={columns} data={quoteSummary?.buying_price || []} emptyState={{ text: strings('no_data_found') }} hideFooter />
			{/* shipping address is an object — rendered as a read-only card (no edit/delete handlers passed) */}
			{!!quoteSummary?.shipping_address && (
				<AddressSection>
					<DetailLabel>{strings('shipping_address')}</DetailLabel>
					<AddressCard data={quoteSummary.shipping_address} />
				</AddressSection>
			)}
		</DrawerBodyWrapper>
	)
}

export default SupplierPoDetails
